/**
 * Section sous-traitant dans le modal d'ajout de coût annexe
 * Affichée conditionnellement quand type_frais === "Sous-traitant"
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronsUpDown, Check, Plus, Trash2, Loader2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSousTraitants } from '@/hooks/useSousTraitants';
import { useContratsST } from '@/hooks/useContratsST';
import { getSpecialiteLabel } from '@/types/sous-traitant';
import type { TypeFactureST } from '@/types/sous-traitant';
import SousTraitantQuickCreate from './SousTraitantQuickCreate';
import ContratSTQuickCreate from './ContratSTQuickCreate';

interface SousTraitantSectionProps {
  entrepriseId: string;
  chantierId: string;
  selectedSTId: string | null;
  onSTChange: (id: string | null) => void;
  selectedContratId: string | null;
  onContratChange: (id: string | null) => void;
  typeFacture: TypeFactureST;
  onTypeFactureChange: (type: TypeFactureST) => void;
  montantSaisi?: number;
}

export default function SousTraitantSection({
  entrepriseId,
  chantierId,
  selectedSTId,
  onSTChange,
  selectedContratId,
  onContratChange,
  typeFacture,
  onTypeFactureChange,
  montantSaisi,
}: SousTraitantSectionProps) {
  const {
    sousTraitants,
    isLoading: loadingST,
    deleteSousTraitant,
    refetch: refetchST,
  } = useSousTraitants(entrepriseId);

  const {
    contrats,
    isLoading: loadingContrats,
    deleteContratST,
    refetch: refetchContrats,
  } = useContratsST(chantierId);

  // État pour les modals de création rapide
  const [showCreateST, setShowCreateST] = useState(false);
  const [showCreateContrat, setShowCreateContrat] = useState(false);

  // État pour les popovers
  const [openSTPopover, setOpenSTPopover] = useState(false);
  const [openContratPopover, setOpenContratPopover] = useState(false);

  // État pour la suppression
  const [deletingSTId, setDeletingSTId] = useState<string | null>(null);
  const [deletingContratId, setDeletingContratId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filtrer les contrats pour le ST sélectionné
  const contratsForST = useMemo(() => {
    if (!selectedSTId) return [];
    return contrats.filter((c) => c.sous_traitant_id === selectedSTId);
  }, [contrats, selectedSTId]);

  // Contrat sélectionné
  const selectedContrat = useMemo(() => {
    if (!selectedContratId) return null;
    return contrats.find((c) => c.contrat_id === selectedContratId) || null;
  }, [contrats, selectedContratId]);

  // ST sélectionné
  const selectedST = useMemo(() => {
    if (!selectedSTId) return null;
    return sousTraitants.find((st) => st.id === selectedSTId) || null;
  }, [sousTraitants, selectedSTId]);

  // Formater les montants
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Gérer la création d'un nouveau ST
  const handleSTCreated = async (newST: { id: string }) => {
    await refetchST();
    onSTChange(newST.id);
    setShowCreateST(false);
  };

  // Gérer la création d'un nouveau contrat
  const handleContratCreated = async (newContrat: { id: string }) => {
    await refetchContrats();
    onContratChange(newContrat.id);
    setShowCreateContrat(false);
  };

  // Supprimer un sous-traitant
  const handleDeleteST = async () => {
    if (!deletingSTId) return;
    setIsDeleting(true);

    const success = await deleteSousTraitant(deletingSTId);
    if (success) {
      // Si c'était le ST sélectionné, désélectionner
      if (selectedSTId === deletingSTId) {
        onSTChange(null);
        onContratChange(null);
      }
    }

    setIsDeleting(false);
    setDeletingSTId(null);
  };

  // Supprimer un contrat
  const handleDeleteContrat = async () => {
    if (!deletingContratId) return;
    setIsDeleting(true);

    const success = await deleteContratST(deletingContratId);
    if (success) {
      // Si c'était le contrat sélectionné, désélectionner
      if (selectedContratId === deletingContratId) {
        onContratChange(null);
      }
    }

    setIsDeleting(false);
    setDeletingContratId(null);
  };

  // Reset du contrat si le ST change ET auto-sélection si un seul contrat existe
  useEffect(() => {
    if (selectedSTId) {
      // Vérifier si le contrat actuel appartient au ST
      if (selectedContratId) {
        const contratBelongsToST = contrats.some(
          (c) => c.contrat_id === selectedContratId && c.sous_traitant_id === selectedSTId
        );
        if (!contratBelongsToST) {
          // Le contrat ne correspond pas au ST, chercher un contrat existant
          const existingContrat = contrats.find((c) => c.sous_traitant_id === selectedSTId);
          onContratChange(existingContrat?.contrat_id || null);
        }
      } else {
        // Pas de contrat sélectionné, auto-sélectionner si un existe
        const existingContrat = contrats.find((c) => c.sous_traitant_id === selectedSTId);
        if (existingContrat) {
          onContratChange(existingContrat.contrat_id);
        }
      }
    }
  }, [selectedSTId, selectedContratId, contrats, onContratChange]);

  // Défaut intelligent du type de facture selon le contrat sélectionné
  useEffect(() => {
    if (!selectedContrat) return;
    if (selectedContrat.nb_factures === 0) {
      onTypeFactureChange('acompte');
    } else {
      onTypeFactureChange('facture');
    }
  }, [selectedContratId]);

  // Auto-détection "Solde" quand le montant saisi = reste à facturer
  useEffect(() => {
    if (!selectedContrat || !montantSaisi || montantSaisi <= 0) return;
    if (Math.abs(montantSaisi - selectedContrat.reste_a_facturer) < 0.01) {
      onTypeFactureChange('solde');
    }
  }, [montantSaisi, selectedContrat?.reste_a_facturer]);

  // Nom du ST à supprimer pour le dialog
  const stToDelete = deletingSTId ? sousTraitants.find((st) => st.id === deletingSTId) : null;
  const contratToDelete = deletingContratId
    ? contrats.find((c) => c.contrat_id === deletingContratId)
    : null;

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      {/* Sélecteur de sous-traitant avec Combobox */}
      <div className="space-y-2">
        <Label>Sous-traitant</Label>
        <Popover open={openSTPopover} onOpenChange={setOpenSTPopover}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={openSTPopover}
              className="w-full justify-between font-normal"
              disabled={loadingST}
            >
              {loadingST ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement...
                </span>
              ) : selectedST ? (
                <span className="flex items-center gap-2 truncate">
                  {selectedST.nom}
                  {selectedST.specialite && (
                    <span className="text-muted-foreground">
                      ({getSpecialiteLabel(selectedST.specialite)})
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Sélectionner un sous-traitant (optionnel)
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Rechercher un sous-traitant..." />
              <CommandList>
                <CommandEmpty>Aucun sous-traitant trouvé</CommandEmpty>
                <CommandGroup>
                  {sousTraitants.map((st) => (
                    <CommandItem
                      key={st.id}
                      value={st.nom}
                      onSelect={() => {
                        onSTChange(st.id);
                        setOpenSTPopover(false);
                      }}
                      className="flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-2">
                        <Check
                          className={cn(
                            'h-4 w-4',
                            selectedSTId === st.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span>{st.nom}</span>
                        {st.specialite && (
                          <span className="text-muted-foreground text-xs">
                            ({getSpecialiteLabel(st.specialite)})
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingSTId(st.id);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setShowCreateST(true);
                      setOpenSTPopover(false);
                    }}
                    className="text-foreground font-medium"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Créer un nouveau sous-traitant
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Sélecteur de contrat (si ST sélectionné) avec Combobox */}
      {selectedSTId && (
        <div className="space-y-2">
          <Label>Contrat associé <span className="text-destructive">*</span></Label>
          <Popover open={openContratPopover} onOpenChange={setOpenContratPopover}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openContratPopover}
                className="w-full justify-between font-normal"
                disabled={loadingContrats}
              >
                {loadingContrats ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Chargement...
                  </span>
                ) : selectedContrat ? (
                  <span className="truncate">
                    {selectedContrat.description_travaux || 'Contrat'} -{' '}
                    {formatCurrency(selectedContrat.montant_contrat_ht)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    Sélectionner ou créer un contrat
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un contrat..." />
                <CommandList>
                  {contratsForST.length === 0 ? (
                    <CommandEmpty>
                      Aucun contrat pour ce sous-traitant sur ce chantier
                    </CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {contratsForST.map((contrat) => (
                        <CommandItem
                          key={contrat.contrat_id}
                          value={contrat.description_travaux || 'contrat'}
                          onSelect={() => {
                            onContratChange(contrat.contrat_id);
                            setOpenContratPopover(false);
                          }}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2">
                            <Check
                              className={cn(
                                'h-4 w-4',
                                selectedContratId === contrat.contrat_id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{contrat.description_travaux || 'Contrat'}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(contrat.montant_contrat_ht)} •{' '}
                                {contrat.pourcentage_facture}% facturé
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingContratId(contrat.contrat_id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  <CommandSeparator />
                  <CommandGroup>
                    {contratsForST.length === 0 ? (
                      // Pas de contrat existant → permettre d'en créer un
                      <CommandItem
                        onSelect={() => {
                          setShowCreateContrat(true);
                          setOpenContratPopover(false);
                        }}
                        className="text-foreground font-medium"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Créer un nouveau contrat
                      </CommandItem>
                    ) : (
                      // Contrat existant → expliquer qu'on ne peut en avoir qu'un
                      <div className="px-2 py-2 text-xs text-muted-foreground">
                        💡 Un seul contrat par sous-traitant par chantier.
                        Pour modifier le montant, supprimez l'ancien contrat.
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Afficher info contrat si sélectionné */}
      {selectedContrat && (
        <div className="rounded-lg border border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-blue-500/10 overflow-hidden">
          {/* En-tête */}
          <div className="px-4 py-2 bg-blue-500/10 border-b border-blue-500/20">
            <p className="text-xs font-medium text-blue-600 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Récapitulatif du contrat
            </p>
          </div>
          {/* Montants */}
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Contrat</p>
                <p className="text-base font-bold text-foreground">
                  {formatCurrency(selectedContrat.montant_contrat_ht)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Déjà facturé</p>
                <p className="text-base font-bold text-blue-600">
                  {formatCurrency(selectedContrat.montant_facture_ht)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reste</p>
                <p className={`text-base font-bold ${selectedContrat.reste_a_facturer > 0 ? 'text-orange-500' : 'text-green-500'}`}>
                  {formatCurrency(selectedContrat.reste_a_facturer)}
                </p>
              </div>
            </div>
            {/* Barre de progression */}
            <div className="space-y-1">
              <Progress value={selectedContrat.pourcentage_facture} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                {selectedContrat.pourcentage_facture}% facturé
                {selectedContrat.nb_factures > 0 && (
                  <> • {selectedContrat.nb_factures} facture{selectedContrat.nb_factures > 1 ? 's' : ''}</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Type de facture ST */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Type de facture</Label>
        <Select value={typeFacture} onValueChange={(v) => onTypeFactureChange(v as TypeFactureST)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="acompte">Acompte</SelectItem>
            <SelectItem value="facture">Facture intermédiaire</SelectItem>
            <SelectItem value="solde">Solde</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Modal création rapide ST */}
      <SousTraitantQuickCreate
        open={showCreateST}
        onOpenChange={setShowCreateST}
        entrepriseId={entrepriseId}
        onSuccess={handleSTCreated}
      />

      {/* Modal création contrat ST */}
      {selectedSTId && (
        <ContratSTQuickCreate
          open={showCreateContrat}
          onOpenChange={setShowCreateContrat}
          chantierId={chantierId}
          sousTraitantId={selectedSTId}
          onSuccess={handleContratCreated}
        />
      )}

      {/* Dialog confirmation suppression ST */}
      <AlertDialog open={!!deletingSTId} onOpenChange={() => setDeletingSTId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le sous-traitant ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer{' '}
              <span className="font-medium text-foreground">{stToDelete?.nom}</span> de votre
              carnet d'adresses ?
              <br />
              <br />
              Cette action est irréversible. Si ce sous-traitant a des contrats en cours, la
              suppression sera impossible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteST}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog confirmation suppression contrat */}
      <AlertDialog open={!!deletingContratId} onOpenChange={() => setDeletingContratId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce contrat
              {contratToDelete?.description_travaux && (
                <>
                  {' '}
                  (<span className="font-medium">{contratToDelete.description_travaux}</span>)
                </>
              )}{' '}
              ?
              <br />
              <br />
              Les factures liées à ce contrat ne seront plus associées mais resteront dans vos
              coûts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContrat}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
